/**
 * /app/lab — Pulsar LAB (PULSE-172).
 *
 * Forward-validation dashboard for our 6 MANUAL lab strategies. Each
 * card pairs the in-sample backtest EXPECTATION with the LIVE
 * forward-demo stats from trading_positions (user 8).
 *
 * Read-only, account-agnostic (fixed lab account). Mirrors the visual
 * language of Slots/Positions: bg-ink cards, pill badges, pulse/rose/
 * amber accents, tabular-nums mono stats. No new design language.
 *
 * Polish (PULSE-172 iteration): RU card text (thesis_ru), per-card
 * status accent (left border + badge), expected-vs-actual HERO metric,
 * sample-maturity cue, RU labels, compact secondary params row.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { ContainerPanel } from "@/components/lab/ContainerPanel";
import { LabTrades } from "@/components/lab/LabTrades";
import { type LabStrategy, type LabLastTradeRecon, type LabMarketState, useLabStrategies, useLabMarketState, useDetectorGates } from "@/hooks/useDashboardApi";
import { cn } from "@/lib/cn";
import { formatPct, timeAgo } from "@/lib/format";

export default function PulsarLab() {
  const { t } = useTranslation("app");
  const { data, isLoading, isError } = useLabStrategies();
  const [showAll, setShowAll] = useState(false);

  return (
    <div className="p-4 sm:p-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("lab.title")}
        </h1>
        <p className="text-sm text-textDim mt-1">{t("lab.subtitle")}</p>
      </header>

      <MarketStrip />
      <DetectorGatesStrip />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 items-start">
        <LabTrades />
        <ContainerPanel />
      </div>

      {isLoading && (
        <div className="px-5 py-10 text-center text-textFaint text-sm bg-ink border border-line">
          …
        </div>
      )}
      {isError && (
        <div className="px-5 py-10 text-center text-rose text-sm bg-ink border border-line">
          Не удалось загрузить лаб-стратегии.
        </div>
      )}
      {data && data.items.length === 0 && (
        <div className="px-5 py-10 text-center text-textFaint text-sm bg-ink border border-line">
          —
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <SummaryStrip items={data.items} />
          {(() => {
            const activeN = data.items.filter((s) => s.is_active).length;
            const inactiveN = data.items.length - activeN;
            if (inactiveN === 0) return null;
            return (
              <div className="flex items-center gap-2 mb-3 text-xs">
                <span className="text-textDim">
                  {showAll ? `Все стратегии (${data.items.length})` : `Активные (${activeN})`}
                </span>
                <button
                  onClick={() => setShowAll((v) => !v)}
                  className="text-fire hover:brightness-110 font-medium"
                >
                  {showAll ? "только активные" : `показать все (${data.items.length})`}
                </button>
              </div>
            );
          })()}
          {dirGroups(showAll ? data.items : data.items.filter((s) => s.is_active)).map((g) => (
            <section key={g.key} className="mb-5">
              <div className="flex items-center gap-2 mb-2">
                <span className={cn("dot", g.dot)} />
                <h2 className="text-xs font-semibold uppercase tracking-wider text-textDim">
                  {g.label}
                </h2>
                <span className="text-2xs font-mono text-textFaint">{g.items.length}</span>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {g.items.map((s) => (
                  <LabCard key={s.strategy_id} s={s} />
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

/* ─── Group by direction (long / short / other), sort within ────────── */

function dirGroups(items: LabStrategy[]) {
  const buckets: Record<string, LabStrategy[]> = { long: [], short: [], other: [] };
  for (const s of items) {
    const k =
      s.trade_direction === "long" ? "long" : s.trade_direction === "short" ? "short" : "other";
    buckets[k].push(s);
  }
  const defs = [
    { key: "long", label: "Лонг", dot: "bg-pulse" },
    { key: "short", label: "Шорт", dot: "bg-rose" },
    { key: "other", label: "Прочее", dot: "bg-textFaint" },
  ];
  return defs
    .map((d) => ({ ...d, items: buckets[d.key].sort(byActivity) }))
    .filter((g) => g.items.length > 0);
}

/* ─── Sort: active + with-data first, then by live total ────────────── */

function byActivity(a: LabStrategy, b: LabStrategy): number {
  const score = (s: LabStrategy) =>
    (s.is_active ? 2 : 0) + (s.live.n_closed > 0 || s.live.n_open > 0 ? 1 : 0);
  const ds = score(b) - score(a);
  if (ds !== 0) return ds;
  return (b.live.total_realized_pnl_pct ?? -1e9) - (a.live.total_realized_pnl_pct ?? -1e9);
}

/* ─── Summary strip — fleet-level glance ────────────────────────────── */

function SummaryStrip({ items }: { items: LabStrategy[] }) {
  const active = items.filter((s) => s.is_active).length;
  const closed = items.reduce((n, s) => n + s.live.n_closed, 0);
  const open = items.reduce((n, s) => n + s.live.n_open, 0);
  const total = items.reduce((n, s) => n + (s.live.total_realized_pnl_pct ?? 0), 0);
  const onPlan = items.filter(
    (s) =>
      s.live.n_closed >= 15 &&
      s.expectation?.expected_mean_pp != null &&
      s.live.avg_realized_pnl_pct != null &&
      s.live.avg_realized_pnl_pct >= s.expectation.expected_mean_pp,
  ).length;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-line border border-line mb-4">
      <SummaryCell label="Стратегий" value={`${items.length}`} sub={`${active} активны`} />
      <SummaryCell label="По плану" value={`${onPlan}`} sub="meet/beat ожидания" accent="text-pulse" />
      <SummaryCell label="Сделок (live)" value={`${closed}`} sub={`${open} открыто`} />
      <SummaryCell
        label="Суммарно PnL"
        value={formatPct(total, { sign: true })}
        sub="сумма по стратам"
        accent={total >= 0 ? "text-pulse" : "text-rose"}
      />
    </div>
  );
}

function SummaryCell({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub: string;
  accent?: string;
}) {
  return (
    <div className="bg-ink px-4 py-3">
      <div className="text-textFaint text-2xs uppercase tracking-wider">{label}</div>
      <div className={cn("font-mono tabular-nums text-xl mt-0.5", accent ?? "text-text")}>{value}</div>
      <div className="text-2xs text-textFaint mt-0.5">{sub}</div>
    </div>
  );
}

/* ─── Status verdict ────────────────────────────────────────────────── */

type Verdict = {
  /** left-border + badge colour family */
  tone: "grey" | "amber" | "pulse" | "rose";
  label: string;
  border: string; // tailwind border-l colour class
  pill: string; // pill bg/text classes
};

/** Per-card status — drives the coloured left border + badge.
 *
 *   n_closed == 0                         → grey  "нет данных"
 *   expected_mean_pp == null              → amber "нет ожидания"
 *   n_closed < 15                         → amber "рано · n={n}"
 *   live avg >= expected                  → green "по плану"
 *   else                                  → rose  "ниже ожиданий"
 */
function verdictOf(s: LabStrategy): Verdict {
  const closed = s.live.n_closed;
  const expMean = s.expectation?.expected_mean_pp ?? null;
  const liveAvg = s.live.avg_realized_pnl_pct;

  if (closed === 0) {
    return {
      tone: "grey",
      label: "нет данных",
      border: "border-l-line",
      pill: "bg-elevated text-textDim",
    };
  }
  if (expMean == null) {
    return {
      tone: "amber",
      label: "нет ожидания",
      border: "border-l-amber",
      pill: "bg-amber-faint text-amber",
    };
  }
  if (closed < 15) {
    return {
      tone: "amber",
      label: `рано · n=${closed}`,
      border: "border-l-amber",
      pill: "bg-amber-faint text-amber",
    };
  }
  if (liveAvg != null && liveAvg >= expMean) {
    return {
      tone: "pulse",
      label: "по плану",
      border: "border-l-pulse",
      pill: "bg-pulse-faint text-pulse",
    };
  }
  return {
    tone: "rose",
    label: "ниже ожиданий",
    border: "border-l-rose",
    pill: "bg-rose-faint text-rose",
  };
}

/* ─── Card ──────────────────────────────────────────────────────────── */

function LabCard({ s }: { s: LabStrategy }) {
  const v = verdictOf(s);

  const dir = s.trade_direction;
  const dirClass =
    dir === "long"
      ? "bg-pulse-faint text-pulse"
      : dir === "short"
        ? "bg-rose-faint text-rose"
        : "bg-elevated text-textDim";
  const dirLabel = dir === "long" ? "лонг" : dir === "short" ? "шорт" : dir;

  // RU thesis is the main description; fall back to English description.
  const thesis = s.expectation?.thesis_ru ?? s.description;

  return (
    <Link
      to={`/app/lab/${s.strategy_id}`}
      className={cn(
        "group bg-ink border border-line border-l-4 flex flex-col",
        "transition-colors hover:border-lineHi hover:bg-surface/30",
        v.border,
      )}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-line">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cn(
              "dot",
              s.is_active ? "bg-pulse animate-pulse" : "bg-textFaint",
            )}
            title={s.is_active ? "активна" : "выключена"}
          />
          <h2 className="font-display text-base tracking-tight group-hover:text-text">{s.name}{s.version != null && <span className="ml-1.5 text-2xs font-mono text-textFaint">v{s.version}</span>}</h2>
          {dir && <span className={cn("pill", dirClass)}>{dirLabel}</span>}
          {s.signal_type && (
            <span className="pill bg-elevated text-textDim">
              {s.signal_type}
            </span>
          )}
          {/* Status badge + open affordance — pushed to the right */}
          <span className={cn("pill ml-auto", v.pill)}>{v.label}</span>
          <ArrowRight
            size={14}
            className="text-textFaint transition-transform group-hover:translate-x-0.5 group-hover:text-textDim"
          />
        </div>
      </div>

      {/* Thesis (RU) — main description */}
      {thesis && (
        <p className="px-4 py-3 text-xs text-textDim leading-relaxed border-b border-line">
          {thesis}
        </p>
      )}

      {/* Expected vs Actual — the hero */}
      <Hero s={s} v={v} />

      {/* Last trade vs backtest reconciliation */}
      <ReconRow r={s.last_trade_recon} />

      {/* Params — compact secondary row */}
      <div className="px-4 py-2.5 grid grid-cols-3 gap-y-2 gap-x-3 text-xs border-t border-line">
        <Param label="TP" value={pct(s.take_profit_pct)} />
        <Param label="SL" value={pct(s.stop_loss_pct)} />
        <Param label="Холд" value={mins(s.max_hold_minutes)} />
        <Param label="Плечо" value={s.leverage != null ? `${s.leverage}x` : "—"} />
        <Param label="Размер" value={pct(s.position_size_pct)} />
        <Param label="Кулдаун" value={mins(s.cooldown_minutes)} />
      </div>
    </Link>
  );
}

/* ─── Last trade vs backtest reconciliation ─────────────────────────── */

export function reconTone(r: LabLastTradeRecon | null): { pill: string; label: string } {
  if (!r || r.bt_pnl == null || r.divergence_pp == null)
    return { pill: "bg-elevated text-textFaint", label: "нет данных" };
  const ad = Math.abs(r.divergence_pp);
  if (r.real_reason === "manual" || (r.note ?? "").includes("ручное"))
    return { pill: "bg-elevated text-textDim", label: "ручное закрытие" };
  if (ad < 0.5) return { pill: "bg-pulse-faint text-pulse", label: "сходится" };
  if (ad < 1.5) return { pill: "bg-amber-500/15 text-amber-400", label: "расхождение" };
  return { pill: "bg-rose-faint text-rose", label: "крупное расхождение" };
}

const sgn = (v: number | null | undefined) =>
  v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;

function ReconRow({ r }: { r: LabLastTradeRecon | null }) {
  const t = reconTone(r);
  if (!r || (r.bt_pnl == null && r.symbol == null)) {
    return (
      <div className="px-4 py-2 border-t border-line flex items-center gap-2 text-2xs text-textFaint">
        Посл. сделка vs бектест: <span className={cn("pill", t.pill)}>{t.label}</span>
      </div>
    );
  }
  return (
    <div className="px-4 py-2 border-t border-line flex items-center gap-x-3 gap-y-1 flex-wrap text-2xs">
      <span className="text-textFaint uppercase tracking-wider text-2xs">посл. сделка</span>
      {r.symbol && <span className="font-mono text-textDim">{r.symbol}</span>}
      {r.account_label && (
        <span className={cn("pill", r.is_real ? "bg-rose-faint text-rose" : "bg-line/40 text-textFaint")}>
          {r.account_label}
        </span>
      )}
      <span className="font-mono tabular-nums text-textDim">реал {sgn(r.real_pnl)}</span>
      <span className="font-mono tabular-nums text-textDim">бт {sgn(r.bt_pnl)}</span>
      <span className="font-mono tabular-nums text-textFaint">Δ {sgn(r.divergence_pp)}</span>
      <span className={cn("pill ml-auto", t.pill)} title={r.note ?? undefined}>{t.label}</span>
    </div>
  );
}

function Param({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-textFaint text-2xs uppercase tracking-wider">
        {label}
      </div>
      <div className="font-mono tabular-nums text-textDim">{value}</div>
    </div>
  );
}

/* ─── Hero: live avg/trade vs expected (focal comparison) ───────────── */

function Hero({ s, v }: { s: LabStrategy; v: Verdict }) {
  const exp = s.expectation;
  const live = s.live;

  const liveAvg = live.avg_realized_pnl_pct;
  const expMean = exp?.expected_mean_pp ?? null;

  // Big live number colour: green if >= expected, rose if below, grey if
  // no data / no expectation to judge against (mirror verdict tone).
  const heroClass =
    liveAvg == null
      ? "text-textFaint"
      : expMean == null
        ? "text-amber"
        : liveAvg >= expMean
          ? "text-pulse"
          : "text-rose";

  const liveWr = live.win_rate;
  const expWr = exp?.expected_wr ?? null;
  const wrClass = compareClass(liveWr, expWr);

  return (
    <div className="px-4 py-3 mt-auto">
      {/* Column headers */}
      <div className="grid grid-cols-[1fr_auto] items-end gap-3 mb-2">
        <div className="text-textFaint text-2xs uppercase tracking-wider">
          Факт · live demo
        </div>
        <div className="text-textFaint text-2xs uppercase tracking-wider text-right">
          Ожидание · in-sample
        </div>
      </div>

      {/* HERO: big live avg/trade · muted expected beside it */}
      <div className="grid grid-cols-[1fr_auto] items-baseline gap-3">
        <div>
          <span
            className={cn(
              "font-mono tabular-nums text-3xl leading-none",
              heroClass,
            )}
          >
            {liveAvg != null ? formatPct(liveAvg, { sign: true }) : "—"}
          </span>
          <span className="text-textFaint text-2xs ml-1.5">средн./сделку</span>
        </div>
        <div className="text-right">
          <div className="font-mono tabular-nums text-sm text-textDim">
            {expMean != null ? formatPct(expMean, { sign: true }) : "—"}
          </div>
        </div>
      </div>

      {/* Sample maturity — live closed vs in-sample n */}
      <SampleMaturity closed={live.n_closed} sampleN={exp?.sample_n ?? null} tone={v.tone} />

      {/* Secondary stats line: winrate (live vs exp) */}
      <div className="flex items-baseline justify-between gap-2 mt-3 pt-2.5 border-t border-line/60 text-2xs font-mono">
        <span className="text-textFaint">винрейт</span>
        <span>
          <span className={cn("tabular-nums", liveWr != null ? wrClass : "text-textFaint")}>
            {liveWr != null ? formatPct(liveWr * 100) : "—"}
          </span>
          <span className="text-textFaint">
            {" "}
            / {expWr != null ? formatPct(expWr * 100) : "—"}
          </span>
        </span>
      </div>

      {/* Footer: total · closed · open · last */}
      <div className="flex items-center gap-x-3 gap-y-1 mt-2 text-2xs font-mono text-textDim flex-wrap">
        <span>
          итого{" "}
          <span
            className={cn(
              "tabular-nums",
              live.total_realized_pnl_pct == null
                ? "text-textFaint"
                : live.total_realized_pnl_pct >= 0
                  ? "text-pulse"
                  : "text-rose",
            )}
          >
            {live.total_realized_pnl_pct != null
              ? formatPct(live.total_realized_pnl_pct, { sign: true })
              : "—"}
          </span>
        </span>
        <span className="text-textFaint">·</span>
        <span>
          закрыто{" "}
          <span className="text-text tabular-nums">{live.n_closed}</span>
        </span>
        <span className="text-textFaint">·</span>
        <span>
          открыто{" "}
          <span className="text-text tabular-nums">{live.n_open}</span>
        </span>
        <span className="text-textFaint">·</span>
        <span className="text-textFaint">
          последняя {timeAgo(live.last_opened_at)} назад
        </span>
      </div>

      {/* Confidence + note */}
      {exp?.confidence && (
        <div className="mt-2.5 flex items-center gap-2 flex-wrap">
          <span className="text-textFaint text-2xs uppercase tracking-wider">
            уверенность
          </span>
          <span className={cn("pill", confidencePill(exp.confidence))}>
            {confidenceLabel(exp.confidence)}
          </span>
        </div>
      )}
      {exp?.note && (
        <p className="mt-2 text-2xs text-textFaint leading-snug">{exp.note}</p>
      )}
    </div>
  );
}

/* ─── Sample maturity cue ───────────────────────────────────────────── */

function SampleMaturity({
  closed,
  sampleN,
  tone,
}: {
  closed: number;
  sampleN: number | null;
  tone: Verdict["tone"];
}) {
  // Mini-bar: live closed as a fraction of in-sample n (capped at 1).
  const frac = sampleN && sampleN > 0 ? Math.min(closed / sampleN, 1) : 0;
  const barClass =
    tone === "pulse"
      ? "bg-pulse"
      : tone === "rose"
        ? "bg-rose"
        : tone === "amber"
          ? "bg-amber"
          : "bg-textFaint";

  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="h-1 flex-1 bg-elevated overflow-hidden rounded-sm">
        <div
          className={cn("h-full", barClass)}
          style={{ width: `${Math.max(frac * 100, closed > 0 ? 3 : 0)}%` }}
        />
      </div>
      <span className="text-2xs text-textFaint font-mono tabular-nums whitespace-nowrap">
        выборка: {closed} / {sampleN != null ? sampleN : "—"}
      </span>
    </div>
  );
}

/* ─── helpers ───────────────────────────────────────────────────────── */

/** Green when live meets/beats expectation, rose when it lags, amber
 *  when there is no expectation to compare against, grey when no data. */
function compareClass(
  actual: number | null | undefined,
  expected: number | null | undefined,
): string {
  if (actual == null) return "text-textFaint";
  if (expected == null) return "text-amber";
  return actual >= expected ? "text-pulse" : "text-rose";
}

function confidenceLabel(c: string): string {
  switch (c) {
    case "medium":
      return "средняя";
    case "frozen":
      return "заморож.";
    case "low":
      return "низкая";
    case "high":
      return "высокая";
    default:
      return c;
  }
}

function confidencePill(c: string): string {
  switch (c) {
    case "frozen":
      return "bg-sky-faint text-sky";
    case "high":
      return "bg-pulse-faint text-pulse";
    case "low":
      return "bg-rose-faint text-rose";
    default: // medium
      return "bg-amber-faint text-amber";
  }
}

function pct(v: number | null | undefined): string {
  return v != null ? `${v}%` : "—";
}

function mins(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v >= 1440 && v % 1440 === 0) return `${v / 1440}d`;
  if (v >= 60 && v % 60 === 0) return `${v / 60}h`;
  return `${v}m`;
}

// ─── Рынок сейчас (market_state_1m, PULSE-223) ───────────────────────

function fngLabel(v: number | null): string {
  if (v == null) return "—";
  if (v < 25) return `${v} страх`;
  if (v < 45) return `${v} осторожно`;
  if (v < 55) return `${v} нейтрально`;
  return `${v} жадность`;
}

function MarketStrip() {
  const { data: st } = useLabMarketState();
  if (!st || st.stale) {
    return (
      <div className="mb-4 px-4 py-2 bg-ink border border-line text-xs text-textFaint">
        Состояние рынка: {st?.stale ? "данные устарели (коллектор?)" : "загрузка…"}
      </div>
    );
  }
  const chip = (label: string, value: string, tone?: "good" | "bad") => (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="text-textFaint">{label}</span>
      <span className={cn(
        "font-mono font-medium",
        tone === "good" && "text-mint", tone === "bad" && "text-rose",
        !tone && "text-text",
      )}>{value}</span>
    </span>
  );
  const s = st as LabMarketState;
  return (
    <div className="mb-4 px-4 py-2.5 bg-ink border border-line flex flex-wrap gap-x-5 gap-y-1.5 text-xs items-center">
      <span className="text-2xs font-semibold uppercase tracking-wider text-textDim">Рынок сейчас</span>
      {chip("BTC 24ч", s.btc_ret_24h != null ? `${s.btc_ret_24h >= 0 ? "+" : ""}${s.btc_ret_24h.toFixed(1)}%` : "—",
        s.btc_ret_24h != null ? (s.btc_ret_24h >= 0 ? "good" : "bad") : undefined)}
      {chip("vol 6ч", s.btc_vol_6h != null ? s.btc_vol_6h.toFixed(3) : "—")}
      {chip("просадка 7д", s.btc_dd_7d != null ? `${s.btc_dd_7d.toFixed(1)}%` : "—",
        s.btc_dd_7d != null ? (s.btc_dd_7d > -4 ? "good" : s.btc_dd_7d < -8 ? "bad" : undefined) : undefined)}
      {chip("breadth", s.breadth_4h != null ? `${Math.round(s.breadth_4h * 100)}%` : "—")}
      {chip("disp", s.disp_4h != null ? s.disp_4h.toFixed(1) : "—")}
      {chip("альты vs BTC", s.alt_relstr_4h != null ? `${s.alt_relstr_4h >= 0 ? "+" : ""}${s.alt_relstr_4h.toFixed(1)}%` : "—")}
      {chip("ликвы L 4ч", s.mkt_longliq_4h != null ? `$${(s.mkt_longliq_4h / 1e6).toFixed(0)}M` : "—")}
      {chip("F&G", fngLabel(s.fng), s.fng != null ? (s.fng < 25 ? "bad" : undefined) : undefined)}
      {chip("BTC 15м", s.btc_ret_15m != null ? `${s.btc_ret_15m >= 0 ? "+" : ""}${s.btc_ret_15m.toFixed(2)}%` : "—")}
      {s.shock_state && s.shock_state !== "calm" && (
        <span className={cn(
          "px-1.5 py-0.5 font-semibold rounded-sm uppercase",
          s.shock_state === "shock_up" && "bg-mint/15 text-mint",
          s.shock_state === "shock_down" && "bg-amber-500/15 text-amber-400",
          s.shock_state === "cascade_down" && "bg-rose/15 text-rose",
        )}>
          {s.shock_state === "cascade_down" ? "КАСКАД ↓" : s.shock_state === "shock_down" ? "ШОК ↓" : "ШОК ↑"}
          {s.liq_usd_15m != null && s.liq_usd_15m > 5e6 ? ` · ликвы $${(s.liq_usd_15m / 1e6).toFixed(0)}M/15м` : ""}
        </span>
      )}
      {s.crash && <span className="px-1.5 py-0.5 bg-rose/15 text-rose font-semibold rounded-sm">CRASH</span>}
    </div>
  );
}


function DetectorGatesStrip() {
  const { data } = useDetectorGates();
  const items = data?.items ?? [];
  if (!items.length) return null;
  const byDet: Record<string, { traded: number; gates: [string, number][] }> = {};
  for (const it of items) {
    const d = (byDet[it.detector] ??= { traded: 0, gates: [] });
    if (it.reason === "traded") d.traded += Number(it.n);
    else d.gates.push([it.reason, Number(it.n)]);
  }
  return (
    <div className="mb-4 px-4 py-2.5 bg-ink border border-line text-xs">
      <span className="text-2xs font-semibold uppercase tracking-wider text-textDim mr-4">
        Гейты детекторов · 7д
      </span>
      {Object.entries(byDet).map(([det, v]) => (
        <span key={det} className="inline-flex items-center gap-1.5 mr-5 whitespace-nowrap">
          <span className="text-text font-mono">{det}</span>
          <span className="text-mint font-mono">торг {v.traded}</span>
          {v.gates.map(([r, n]) => (
            <span key={r} className="text-textFaint font-mono">{r}:{n}</span>
          ))}
        </span>
      ))}
    </div>
  );
}
